import { useState, useEffect, useRef } from "react";
import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useAppSettings } from "@/contexts/AppSettingsContext";
import { useLoginRedirectHandler } from "@/hooks/useLoginRedirectHandler";
import { useToast } from "@/hooks/useToast";
import { useLogger } from "@/hooks/useLogger";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { useOrganizations, useCurrentOrganization } from "@/hooks/organization";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { useHubProfile } from "@/hooks/hub/useHubProfile";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/application/queries";
import { ROUTES } from "@/app/routes";
import { cn } from "@/utils";
import { LoadingSpinner } from "@/components/ui";
import { GlobalToastContainer } from "@/components/ui/GlobalToastContainer";
import { ToastContainer } from "@/components/ui/Toast";
import { Sidebar } from "@/components/shared/navigation/Sidebar";
import { TopBar } from "@/components/shared/navigation/TopBar";
import { TopBarActions } from "@/components/shared/navigation/TopBar/TopBarActions";
import { useSidebar } from "@/components/shared/navigation/hooks/useSidebar";
import { CriticalErrorState } from "@/components/feedback";
import { SuspenseOutlet } from "@/components/feedback/SuspenseOutlet";
import { AiAgentChat } from "@/components/feature";
import { HubBottomNav } from "@/components/shared/navigation/HubBottomNav";
import type { AppLayoutConfig } from "./layoutConfigs";
import logoSmall from "@/assets/hulahoop-logo-mini-pos.svg";

interface AppLayoutProps {
	config: AppLayoutConfig;
}

export default function AppLayout({ config }: AppLayoutProps) {
	return <AppLayoutContent config={config} />;
}

// --- Sub-components for conditional hook usage ---

function OrgContextSidebar(
	props: React.ComponentProps<typeof Sidebar> & Record<string, unknown>,
) {
	const { organizationDetails, userOrganizations } = useOrganizations();
	const currentOrganization = useCurrentOrganization();
	return (
		<Sidebar
			{...props}
			organizationDetails={organizationDetails}
			currentOrganization={currentOrganization}
			userOrganizations={userOrganizations}
		/>
	);
}

function AiChatWidget() {
	const { hasModule } = useModuleAccess();
	if (!hasModule("ai_agent")) return null;
	return <AiAgentChat />;
}

interface ProfileSwitchManagerProps {
	mainContentRef: React.RefObject<HTMLDivElement | null>;
}

function ProfileSwitchManager({ mainContentRef }: ProfileSwitchManagerProps) {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { selectedProfileUuid, isSwitchingProfile, clearSwitchingState } =
		useHubProfile();
	const prevProfileRef = useRef<string | null>(null);

	useEffect(() => {
		if (
			prevProfileRef.current !== null &&
			prevProfileRef.current !== selectedProfileUuid
		) {
			queryClient.invalidateQueries({ queryKey: queryKeys.hub.all });
			const timer = setTimeout(() => {
				clearSwitchingState();
				if (mainContentRef.current) {
					mainContentRef.current.focus();
				}
			}, 300);
			return () => clearTimeout(timer);
		}
		prevProfileRef.current = selectedProfileUuid;
	}, [selectedProfileUuid, queryClient, clearSwitchingState, mainContentRef]);

	if (!isSwitchingProfile) return null;
	return (
		<SwitchingOverlay
			message={t("hub.profile_selector.switching", "Cambiando perfil...")}
		/>
	);
}

function SwitchingOverlay({ message }: { message: string }) {
	return (
		<div className="absolute inset-0 bg-bg-primary/80 backdrop-blur-sm z-40 flex items-center justify-center">
			<div className="text-center">
				<LoadingSpinner size="md" variant="primary" className="mx-auto mb-3" />
				<p className="text-sm text-text-secondary">{message}</p>
			</div>
		</div>
	);
}

/** Only render under enableProfileSwitching (hub layout) — calls useHubProfile which triggers TQ fetch. */
function HubCriticalErrorGuard() {
	const { error, profiles, isLoading } = useHubProfile();

	if (error && profiles.length === 0 && !isLoading) {
		return (
			<CriticalErrorState
				title="Failed to Load Profiles"
				message={error}
				actionLabel="Reload Page"
				onAction={() => window.location.reload()}
			/>
		);
	}
	return null;
}

function SkipLink() {
	const { t } = useTranslation();
	return (
		<a
			href="#main-content"
			className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-popover focus:px-4 focus:py-2 focus:bg-primary-hover focus:text-text-inverse focus:rounded-md focus:text-sm focus:font-medium"
		>
			{t("hub.accessibility.skip_to_content", "Saltar al contenido")}
		</a>
	);
}

// --- Main layout content ---

function AppLayoutContent({ config }: AppLayoutProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { logout, user } = useAuth();
	const logger = useLogger();
	const isMobile = useIsMobile();
	const { isCollapsed, toggleCollapse, expand } = useSidebar();
	const [showUserMenu, setShowUserMenu] = useState(false);
	useAppSettings();
	const { toasts, dismissToast } = useToast();
	useLoginRedirectHandler();

	const mainContentRef = useRef<HTMLDivElement | null>(null);

	const navItems = config.navItems ?? config.getNavItems?.(t);

	const handleLogout = async () => {
		try {
			await logout();
			navigate(ROUTES.AUTH.LOGIN);
		} catch (error) {
			logger.error("Logout failed:", error);
		}
	};

	const baseSidebarProps = {
		user,
		showUserMenu,
		onToggleUserMenu: () => setShowUserMenu(!showUserMenu),
		variant: config.variant,
		navItems,
		isCollapsed,
		onToggleCollapse: toggleCollapse,
		onExpand: expand,
		onLogout: handleLogout,
	} as const;

	// Mobile layout (hub only)
	if (config.enableMobileLayout && isMobile) {
		return (
			<div className="flex flex-col h-screen bg-app overflow-x-hidden">
				<SkipLink />
				<header className="bg-bg-primary border-b border-border-secondary px-4 py-3 flex items-center justify-between h-14 flex-shrink-0">
					<div className="flex items-center gap-3">
						<img src={logoSmall} alt="Hulahoop" className="h-7" />
					</div>
					<TopBarActions hideSearch />
				</header>
				<main
					id="main-content"
					ref={mainContentRef}
					className="flex-1 overflow-auto pb-16 relative"
					tabIndex={-1}
				>
					{config.enableProfileSwitching && <HubCriticalErrorGuard />}
					<SuspenseOutlet />
					{config.enableProfileSwitching && (
						<ProfileSwitchManager mainContentRef={mainContentRef} />
					)}
				</main>
				<HubBottomNav />
				<ToastContainer
					toasts={toasts}
					onDismiss={dismissToast}
					showCloseButton
				/>
				<GlobalToastContainer />
			</div>
		);
	}

	// Desktop layout (all variants)
	const SidebarComponent = config.enableOrgContext
		? OrgContextSidebar
		: Sidebar;

	return (
		<div
			className="flex h-screen bg-app overflow-x-hidden"
			style={
				{ "--sidebar-width": isCollapsed ? "70px" : "240px" } as CSSProperties
			}
		>
			<SkipLink />
			<SidebarComponent {...baseSidebarProps} />

			<main
				className={cn(
					"flex-1 flex flex-col transition-[margin-left] duration-200 ease-out overflow-x-hidden",
					isCollapsed ? "ml-[70px]" : "ml-[240px]",
				)}
			>
				{config.disableSearch && config.variant === "hub" ? (
					<div className="flex items-center justify-end bg-bg-primary border-b border-border-secondary h-14 px-4">
						<TopBarActions hideSearch />
					</div>
				) : (
					<TopBar hideSearch={config.disableSearch} />
				)}

				<div
					id="main-content"
					ref={mainContentRef}
					className="flex-1 overflow-auto relative"
					tabIndex={config.enableProfileSwitching ? -1 : undefined}
				>
					{config.enableProfileSwitching && <HubCriticalErrorGuard />}
					<SuspenseOutlet />
					{config.enableProfileSwitching && (
						<ProfileSwitchManager mainContentRef={mainContentRef} />
					)}
				</div>
			</main>

			<ToastContainer
				toasts={toasts}
				onDismiss={dismissToast}
				showCloseButton
			/>
			<GlobalToastContainer />
			{config.enableAiChat && <AiChatWidget />}
		</div>
	);
}
